import type { IAuditLog } from '../../../src/engine/AuditLog';
import type { IRecordStore } from '../../../src/engine/UndoEngine';
import type { IOperationLogEntry } from '../../../src/models/Operation';
import type { FieldValue, IFields, IRecord, ListStore } from './ListStore';

export interface IRecordWriterOptions {
  /** History is kept per library: the library the firm's files live in. */
  libraryId: string;
  libraryName: string;
  user: string;
  now?: () => Date;
  idSuffix?: () => string;
}

function pad(n: number, width: number): string {
  let s: string = String(n);
  while (s.length < width) {
    s = `0${s}`;
  }
  return s;
}

function same(a: FieldValue, b: FieldValue): boolean {
  const blank = (v: FieldValue): boolean => v === undefined || v === null || String(v).trim() === '';
  return (blank(a) ? '' : String(a)) === (blank(b) ? '' : String(b));
}

/**
 * Changes to the firm's records, each one logged in History (in the firm's own site) so it can
 * be seen and undone, like changes to files. A group of changes made together is one batch.
 */
export class RecordWriter implements IRecordStore {
  private readonly _stores: { [urlName: string]: ListStore } = {};

  constructor(private readonly _audit: IAuditLog, stores: ListStore[], private readonly _o: IRecordWriterOptions) {
    for (const s of stores) {
      this._stores[s.schema.urlName] = s;
    }
  }

  /** Starts a group of changes that show (and undo) together in History. */
  public batch(): RecordBatch {
    return new RecordBatch(this._audit, this._o);
  }

  public store(urlName: string): ListStore {
    const s: ListStore | undefined = this._stores[urlName];
    if (!s) {
      throw new Error(`Unknown list ${urlName}.`);
    }
    return s;
  }

  public getFields(list: string, itemId: string, columns: string[]): Promise<{ [column: string]: unknown } | undefined> {
    return this.store(list).fieldsOf(itemId, columns);
  }

  public setFields(list: string, itemId: string, fields: { [column: string]: unknown }): Promise<void> {
    return this.store(list).update(itemId, fields as IFields);
  }
}

export class RecordBatch {
  public readonly batchId: string;
  public readonly entries: IOperationLogEntry[] = [];
  private readonly _day: string;
  private readonly _suffix: string;
  private _ready: boolean = false;

  constructor(private readonly _audit: IAuditLog, private readonly _o: IRecordWriterOptions) {
    const started: string = this._now().toISOString();
    this._day = started.substring(0, 10).replace(/-/g, '');
    this._suffix = (this._o.idSuffix ?? (() => Math.random().toString(36).substring(2, 6).toUpperCase()))();
    this.batchId = `BATCH-${this._day}-${started.substring(11, 19).replace(/:/g, '')}-${this._suffix}`;
  }

  /**
   * Adds a row. In History it reads as `reason`; undoing it marks the row Removed (never deleted).
   * @param label what the row is, in a few words ("Passport (Khalil, Omar)").
   */
  public async create(store: ListStore, fields: IFields, label: string, reason: string): Promise<IRecord> {
    await this._ensure();
    const record: IRecord = await store.create({ ...fields, Removed: false });
    await this._log(store, record, { Removed: true }, { Removed: false }, label, reason);
    return record;
  }

  /**
   * Changes some values of a row, if they differ from what's there now. Returns false when
   * nothing needed changing (nothing is logged then).
   */
  public async update(store: ListStore, record: IRecord, fields: IFields, label: string, reason: string): Promise<boolean> {
    await this._ensure();
    const columns: string[] = Object.keys(fields);
    const current: IFields = (await store.fieldsOf(record.id, columns)) ?? {};
    const changed: string[] = columns.filter(c => !same(current[c], fields[c]));
    if (!changed.length) {
      return false;
    }
    const before: IFields = {};
    const after: IFields = {};
    for (const c of changed) {
      before[c] = current[c];
      after[c] = fields[c];
    }
    await store.update(record.id, after);
    await this._log(store, record, before, after, label, reason);
    return true;
  }

  /** Records that someone opened a record (restricted files): seen in their history, nothing to undo. */
  public async viewed(store: ListStore, record: IRecord, label: string, reason: string): Promise<void> {
    await this._ensure();
    const list: string = store.schema.urlName;
    const entry: IOperationLogEntry = {
      operationId: `OP-${this._day}-${this._suffix}-${pad(this.entries.length + 1, 4)}`,
      batchId: this.batchId, changeId: `view:${list}:${record.key}`, action: 'view', itemKind: 'record', itemId: record.id,
      libraryId: this._o.libraryId, libraryName: this._o.libraryName, sourcePath: '', sourceParentId: list, originalName: label,
      destinationPath: '', destinationParentId: list, newName: record.key, reason, status: 'success', reversible: false,
      user: this._o.user, timestamp: this._now().toISOString()
    };
    this.entries.push(entry);
    await this._audit.write(entry);
  }

  /** Marks a row Removed (it stays in the list, and Undo brings it back). */
  public remove(store: ListStore, record: IRecord, label: string, reason: string): Promise<boolean> {
    return this.update(store, record, { Removed: true }, label, reason);
  }

  private _now(): Date {
    return this._o.now ? this._o.now() : new Date();
  }

  private async _ensure(): Promise<void> {
    if (!this._ready) {
      await this._audit.ensure();
      this._ready = true;
    }
  }

  private async _log(store: ListStore, record: IRecord, before: IFields, after: IFields, label: string, reason: string): Promise<void> {
    const list: string = store.schema.urlName;
    const entry: IOperationLogEntry = {
      operationId: `OP-${this._day}-${this._suffix}-${pad(this.entries.length + 1, 4)}`,
      batchId: this.batchId,
      changeId: `record:${list}:${record.key}`,
      action: 'record',
      itemKind: 'record',
      itemId: record.id,
      libraryId: this._o.libraryId,
      libraryName: this._o.libraryName,
      sourcePath: JSON.stringify(before),
      sourceParentId: list,
      originalName: label,
      destinationPath: JSON.stringify(after),
      destinationParentId: list,
      newName: record.key,
      reason,
      status: 'success',
      reversible: true,
      user: this._o.user,
      timestamp: this._now().toISOString()
    };
    this.entries.push(entry);
    await this._audit.write(entry);
  }
}
